import { type Store } from 'tinybase';

import {
  type SearchPerformancePagesRow,
  type SearchPerformancePagesSourceType,
} from '../api/searchPerformanceApi';
import { type PagesMetricState } from './PagesFormatting';

/**
 * One row per page, keyed by `pageId`. Each source writes only the cells it
 * owns (visibility column ownership, D050), so the row is the union of the
 * two writes and never an aggregate. A cell a source did not write is absent
 * — TinyBase has no null — and reads back as null.
 */
export const PAGES_TABLE = 'pages';

export const SOURCE_PRESENCE_CELL: Record<SearchPerformancePagesSourceType, string> = {
  GSC: 'inGsc',
  PRERENDER: 'inPrerender',
};

const SOURCE_OWNED_CELLS: Record<SearchPerformancePagesSourceType, readonly string[]> = {
  GSC: ['metricState', 'impressions', 'clicks', 'ctrPct', 'inspectionVerdict', 'coverageState', 'inspectedAt'],
  PRERENDER: ['lastStatusCode', 'lastCrawledAt'],
};

type Cells = Record<string, string | number | boolean>;

function toOwnedCells(source: SearchPerformancePagesSourceType, row: SearchPerformancePagesRow): Cells {
  const cells: Cells = {
    url: row.url,
    sectionKey: row.sectionKey,
    [SOURCE_PRESENCE_CELL[source]]: true,
  };
  if (source === 'GSC') {
    if (row.metricState !== null) cells.metricState = row.metricState;
    if (row.impressions !== null) cells.impressions = row.impressions;
    if (row.clicks !== null) cells.clicks = row.clicks;
    if (row.ctrPct !== null) cells.ctrPct = row.ctrPct;
    if (row.inspectionVerdict !== null) cells.inspectionVerdict = row.inspectionVerdict;
    if (row.coverageState !== null) cells.coverageState = row.coverageState;
    if (row.inspectedAt !== null) cells.inspectedAt = row.inspectedAt;
  } else {
    if (row.lastStatusCode !== null) cells.lastStatusCode = row.lastStatusCode;
    if (row.lastCrawledAt !== null) cells.lastCrawledAt = row.lastCrawledAt;
  }
  return cells;
}

/**
 * A listing is a snapshot of one source: its previous cells are cleared from
 * every row before the new rows are written, so a page that dropped out of
 * the listing loses that source's columns. A row keeps living while the other
 * source still lists it; it is removed only when neither does.
 */
export function loadSourceRows(
  store: Store,
  source: SearchPerformancePagesSourceType,
  rows: SearchPerformancePagesRow[]
): void {
  const clearedCells = [SOURCE_PRESENCE_CELL[source], ...SOURCE_OWNED_CELLS[source]];
  store.transaction(() => {
    store.getRowIds(PAGES_TABLE).forEach((rowId) => {
      clearedCells.forEach((cellId) => store.delCell(PAGES_TABLE, rowId, cellId));
      const listedElsewhere = Object.values(SOURCE_PRESENCE_CELL).some((presenceCell) =>
        store.hasCell(PAGES_TABLE, rowId, presenceCell)
      );
      if (!listedElsewhere) store.delRow(PAGES_TABLE, rowId);
    });
    rows.forEach((row) => {
      store.setPartialRow(PAGES_TABLE, row.pageId, toOwnedCells(source, row));
    });
  });
}

export type PagesJoinedRow = {
  pageId: string;
  url: string;
  metricState: PagesMetricState;
  impressions: number | null;
  clicks: number | null;
  ctrPct: number | null;
  lastStatusCode: number | null;
  lastCrawledAt: string | null;
  inspectionVerdict: string | null;
  coverageState: string | null;
  inspectedAt: string | null;
};

export function readJoinedRow(store: Store, pageId: string): PagesJoinedRow {
  const cells = store.getRow(PAGES_TABLE, pageId);
  return {
    pageId,
    url: typeof cells.url === 'string' ? cells.url : '',
    metricState: typeof cells.metricState === 'string' ? (cells.metricState as PagesMetricState) : null,
    impressions: typeof cells.impressions === 'number' ? cells.impressions : null,
    clicks: typeof cells.clicks === 'number' ? cells.clicks : null,
    ctrPct: typeof cells.ctrPct === 'number' ? cells.ctrPct : null,
    lastStatusCode: typeof cells.lastStatusCode === 'number' ? cells.lastStatusCode : null,
    lastCrawledAt: typeof cells.lastCrawledAt === 'string' ? cells.lastCrawledAt : null,
    inspectionVerdict: typeof cells.inspectionVerdict === 'string' ? cells.inspectionVerdict : null,
    coverageState: typeof cells.coverageState === 'string' ? cells.coverageState : null,
    inspectedAt: typeof cells.inspectedAt === 'string' ? cells.inspectedAt : null,
  };
}
